import React from 'react';
import { CONTACT_INFO } from '../constants';
import { Facebook, Instagram, Twitter, Mail, Phone } from 'lucide-react';

interface FooterProps {
  onNavigate: (sectionId: string) => void;
  onNavigateToPolicy: (section: 'shipping' | 'refund') => void;
}

export const Footer: React.FC<FooterProps> = ({ onNavigate, onNavigateToPolicy }) => {
  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, sectionId: string) => {
    e.preventDefault();
    onNavigate(sectionId);
  };

  const handlePolicyClick = (e: React.MouseEvent<HTMLAnchorElement>, section: 'shipping' | 'refund') => {
    e.preventDefault();
    onNavigateToPolicy(section);
  };

  return (
    <footer className="bg-earth-800 text-earth-100 pt-16 pb-8">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          
          {/* Brand Info */}
          <div>
            <h3 className="text-2xl font-heading font-bold text-white mb-6">Age Life</h3>
            <p className="text-earth-200 text-sm mb-6 leading-relaxed">
              Bringing authentic, certified Indonesian herbal wellness to East Africa. Experience the healing power of Jamu tradition.
            </p>
            <div className="flex space-x-4">
              <a href="#" className="bg-earth-100/10 p-2 rounded-full hover:bg-turmeric-500 hover:text-herbal-900 transition-all">
                <Facebook className="h-5 w-5" />
              </a>
              <a href="#" className="bg-earth-100/10 p-2 rounded-full hover:bg-turmeric-500 hover:text-herbal-900 transition-all">
                <Instagram className="h-5 w-5" />
              </a>
              <a href="#" className="bg-earth-100/10 p-2 rounded-full hover:bg-turmeric-500 hover:text-herbal-900 transition-all">
                <Twitter className="h-5 w-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-bold text-white mb-6">Quick Links</h4>
            <ul className="space-y-3 text-sm">
              <li><a href="#home" onClick={(e) => handleNavClick(e, 'home')} className="hover:text-turmeric-500 transition-colors">Home</a></li>
              <li><a href="#products" onClick={(e) => handleNavClick(e, 'products')} className="hover:text-turmeric-500 transition-colors">All Products</a></li>
              <li><a href="#about" onClick={(e) => handleNavClick(e, 'about')} className="hover:text-turmeric-500 transition-colors">About Us</a></li>
              <li><a href="#shipping" onClick={(e) => handlePolicyClick(e, 'shipping')} className="hover:text-turmeric-500 transition-colors">Shipping Policy</a></li>
              <li><a href="#refund" onClick={(e) => handlePolicyClick(e, 'refund')} className="hover:text-turmeric-500 transition-colors">Refund Policy</a></li>
            </ul>
          </div>

          {/* Categories */}
          <div>
            <h4 className="text-lg font-bold text-white mb-6">Wellness Categories</h4>
            <ul className="space-y-3 text-sm">
              <li><a href="#products" onClick={(e) => handleNavClick(e, 'products')} className="hover:text-turmeric-500 transition-colors">Pain & Mobility</a></li>
              <li><a href="#products" onClick={(e) => handleNavClick(e, 'products')} className="hover:text-turmeric-500 transition-colors">Digestive & Metabolic</a></li>
              <li><a href="#products" onClick={(e) => handleNavClick(e, 'products')} className="hover:text-turmeric-500 transition-colors">Women's Wellness</a></li>
              <li><a href="#products" onClick={(e) => handleNavClick(e, 'products')} className="hover:text-turmeric-500 transition-colors">Heart & Circulation</a></li>
              <li><a href="#products" onClick={(e) => handleNavClick(e, 'products')} className="hover:text-turmeric-500 transition-colors">Skin & Immunity</a></li>
              <li><a href="#products" onClick={(e) => handleNavClick(e, 'products')} className="hover:text-turmeric-500 transition-colors">Sleep & Calm</a></li>
              <li><a href="#products" onClick={(e) => handleNavClick(e, 'products')} className="hover:text-turmeric-500 transition-colors">Liver & Detox</a></li>
              <li><a href="#products" onClick={(e) => handleNavClick(e, 'products')} className="hover:text-turmeric-500 transition-colors">Vitality & Energy</a></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-lg font-bold text-white mb-6">Contact Us</h4>
            <ul className="space-y-4 text-sm">
              <li className="flex items-start">
                <Phone className="h-5 w-5 mr-3 text-turmeric-500 flex-shrink-0" />
                <span>
                  <a href={`tel:${CONTACT_INFO.phone}`} className="hover:text-white block">{CONTACT_INFO.phoneDisplay}</a>
                  <span className="text-xs text-earth-200 block mt-1">24/7 Customer Service</span>
                </span>
              </li>
              <li className="flex items-center">
                <Mail className="h-5 w-5 mr-3 text-turmeric-500 flex-shrink-0" />
                <a href={`mailto:${CONTACT_INFO.email}`} className="hover:text-white">{CONTACT_INFO.email}</a>
              </li>
              <li className="flex items-start mt-4">
                <p className="text-xs text-earth-200">
                  Headquartered in Nairobi, shipping to Uganda, Tanzania, Rwanda, Somalia, and Ethiopia.
                </p>
              </li>
            </ul>
          </div>

        </div>

        <div className="border-t border-earth-100/10 pt-8 flex flex-col md:flex-row justify-between items-center text-xs text-earth-200">
          <p>&copy; {new Date().getFullYear()} Age Life Herbal Wellness. All rights reserved.</p>
          <div className="flex space-x-6 mt-4 md:mt-0">
            <span className="flex items-center gap-1">GMP Certified</span>
            <span className="flex items-center gap-1">ISO Standard</span>
            <span className="flex items-center gap-1">HALAL</span>
          </div>
        </div>
      </div>
    </footer>
  );
};